import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Building2, Users, IndianRupee, Briefcase, GraduationCap, Globe, Trophy,
  ArrowUpRight, ArrowDownRight, BookOpen, RefreshCw, Plus, Eye, Loader2,
  TrendingUp, Activity, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardHome = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCenters: 0,
    activeCenters: 0,
    totalStudents: 0,
    totalRevenue: 0,
    totalPlacements: 0,
    activeCourses: 0,
    totalEmployers: 0,
    totalJobs: 0,
    pendingApplications: 0
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [recentStudents, setRecentStudents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [commissionReport, setCommissionReport] = useState(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      // Fetch all data in parallel
      const [
        centersRes,
        studentsRes,
        coursesRes,
        employersRes,
        jobsRes,
        applicationsRes,
        commissionRes
      ] = await Promise.all([
        axios.get(`${API}/admin/centers`, { headers }),
        axios.get(`${API}/admin/users?role=student`, { headers }),
        axios.get(`${API}/courses`, { headers }),
        axios.get(`${API}/admin/users?role=employer`, { headers }),
        axios.get(`${API}/jobs`, { headers }),
        axios.get(`${API}/admin/franchise-applications`, { headers }),
        axios.get(`${API}/admin/commission-report`, { headers }).catch(() => ({ data: null }))
      ]);

      const centers = centersRes.data || [];
      const students = studentsRes.data || [];
      const courses = coursesRes.data || [];
      const employers = employersRes.data || [];
      const jobs = jobsRes.data || [];
      const applications = applicationsRes.data || [];

      // Calculate stats
      setStats({
        totalCenters: centers.length,
        activeCenters: centers.filter(c => c.status === 'active').length,
        totalStudents: students.length,
        totalRevenue: commissionRes.data?.total_revenue || 0,
        totalPlacements: 11500, // Placeholder - can be fetched from API
        activeCourses: courses.filter(c => c.is_active).length,
        totalEmployers: employers.length,
        totalJobs: jobs.length,
        pendingApplications: applications.filter(a => a.status === 'pending').length
      });

      // Set recent data
      setRecentApplications(applications.slice(0, 5));
      setRecentStudents(students.slice(0, 5));
      setCommissionReport(commissionRes.data);

      // Mock recent activity
      setRecentActivity([
        { id: 1, type: 'payment', message: 'Fee collected: ₹4,000', time: '5 min ago' },
        { id: 2, type: 'student', message: 'New student enrolled', time: '15 min ago' },
        { id: 3, type: 'center', message: 'Center approved: ABC Institute', time: '1 hour ago' },
        { id: 4, type: 'payment', message: 'Fee collected: ₹6,000', time: '2 hours ago' },
      ]);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-home">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="text-slate-500 mt-1">Welcome back, {user?.name || 'Admin'}</p>
        </div>
        <Button onClick={fetchDashboardData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link to="/admin/courses">
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
            <Plus className="w-4 h-4" /> Add Course
          </Button>
        </Link>
        <Link to="/admin/franchise">
          <Button className="bg-purple-600 hover:bg-purple-700 gap-2">
            <Building2 className="w-4 h-4" /> Add Partner
          </Button>
        </Link>
        {stats.pendingApplications > 0 && (
          <Link to="/admin/franchise">
            <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 gap-2">
              <Building2 className="w-4 h-4" />
              Approve Franchise
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {stats.pendingApplications}
              </span>
            </Button>
          </Link>
        )}
        <Link to="/admin/reports">
          <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50 gap-2">
            <TrendingUp className="w-4 h-4" /> View Reports
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Link to="/admin/franchise" className="block">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-200 text-sm">Total Centers</p>
                <p className="text-3xl font-bold mt-1">{stats.totalCenters}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{stats.activeCenters} active</span>
                </div>
              </div>
              <Building2 className="w-8 h-8 text-blue-300" />
            </div>
          </div>
        </Link>

        <Link to="/admin/students" className="block">
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-200 text-sm">Total Students</p>
                <p className="text-3xl font-bold mt-1">{stats.totalStudents}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-3 h-3" />
                  <span className="text-xs">+12% growth</span>
                </div>
              </div>
              <Users className="w-8 h-8 text-emerald-300" />
            </div>
          </div>
        </Link>

        <Link to="/admin/finance" className="block">
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-purple-200 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold mt-1">₹{(stats.totalRevenue / 100000).toFixed(1)}L</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-3 h-3" />
                  <span className="text-xs">+8% this month</span>
                </div>
              </div>
              <IndianRupee className="w-8 h-8 text-purple-300" />
            </div>
          </div>
        </Link>

        <Link to="/admin/placements" className="block">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-amber-100 text-sm">Total Placements</p>
                <p className="text-3xl font-bold mt-1">{stats.totalPlacements.toLocaleString()}+</p>
                <div className="flex items-center gap-1 mt-2">
                  <Trophy className="w-3 h-3" />
                  <span className="text-xs">85% rate</span>
                </div>
              </div>
              <Briefcase className="w-8 h-8 text-amber-200" />
            </div>
          </div>
        </Link>

        <Link to="/admin/courses" className="block">
          <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-cyan-200 text-sm">Active Courses</p>
                <p className="text-3xl font-bold mt-1">{stats.activeCourses}</p>
                <div className="flex items-center gap-1 mt-2">
                  <BookOpen className="w-3 h-3" />
                  <span className="text-xs">{stats.activeCourses} total</span>
                </div>
              </div>
              <GraduationCap className="w-8 h-8 text-cyan-300" />
            </div>
          </div>
        </Link>

        <Link to="/admin/employers" className="block">
          <div className="bg-gradient-to-br from-pink-600 to-pink-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-pink-200 text-sm">Employers</p>
                <p className="text-3xl font-bold mt-1">{stats.totalEmployers}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Briefcase className="w-3 h-3" />
                  <span className="text-xs">{stats.totalJobs} jobs</span>
                </div>
              </div>
              <Globe className="w-8 h-8 text-pink-300" />
            </div>
          </div>
        </Link>
      </div>

      {/* Revenue & Activity Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue Panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-500" />
            Revenue Overview
          </h3>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-sm">Total Platform Revenue</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                ₹{((commissionReport?.total_revenue || 0) / 100000).toFixed(2)}L
              </p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4">
              <p className="text-emerald-600 text-sm">Iqra Share (40%)</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">
                ₹{((commissionReport?.iqra_share || 0) / 100000).toFixed(2)}L
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-amber-600 text-sm">Pending Payouts</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">
                ₹{((commissionReport?.pending_amount || 0) / 1000).toFixed(0)}K
              </p>
            </div>
          </div>
        </div>

        {/* Live Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            Live Activity
          </h3>
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activity.type === 'payment' ? 'bg-emerald-100' :
                  activity.type === 'student' ? 'bg-blue-100' :
                  'bg-purple-100'
                }`}>
                  {activity.type === 'payment' ? <IndianRupee className="w-4 h-4 text-emerald-600" /> :
                   activity.type === 'student' ? <Users className="w-4 h-4 text-blue-600" /> :
                   <Building2 className="w-4 h-4 text-purple-600" />}
                </div>
                <div>
                  <p className="text-sm text-slate-700">{activity.message}</p>
                  <p className="text-xs text-slate-400">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Data Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Franchise Applications */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              Recent Franchise Applications
            </h3>
            <Link to="/admin/franchise" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View All
            </Link>
          </div>
          
          <div className="space-y-3">
            {recentApplications.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No recent applications</p>
            ) : (
              recentApplications.map((app) => (
                <div key={app.id} className={`rounded-xl p-4 ${
                  app.status === 'pending' ? 'bg-amber-50 border border-amber-200' :
                  app.status === 'stage1_approved' ? 'bg-blue-50 border border-blue-200' :
                  app.status === 'active' ? 'bg-emerald-50 border border-emerald-200' :
                  'bg-slate-50 border border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">{app.center_name || `${app.owner_name}'s Center`}</h4>
                      <p className="text-sm text-slate-500">{app.city}, {app.state}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      app.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                      app.status === 'stage1_approved' ? 'bg-blue-200 text-blue-800' :
                      app.status === 'active' ? 'bg-emerald-200 text-emerald-800' :
                      'bg-slate-200 text-slate-800'
                    }`}>
                      {app.status === 'pending' ? 'Pending' :
                       app.status === 'stage1_approved' ? 'Stage 1' :
                       app.status === 'active' ? 'Active' : app.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Students */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Recent Students
            </h3>
            <Link to="/admin/students" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View All
            </Link>
          </div>
          
          <div className="space-y-3">
            {recentStudents.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No recent students</p>
            ) : (
              recentStudents.map((student) => (
                <div key={student.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium">{student.name?.charAt(0) || 'S'}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{student.name}</p>
                      <p className="text-sm text-slate-500">{student.email}</p>
                    </div>
                  </div>
                  <Link to={`/admin/students?id=${student.id}`}>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardHome;
